import { createBrowserHistory } from 'history';
import {
  Angulartics2Hubspot,
  Angulartics2Mixpanel,
  Angulartics2Segment
} from '@angularticsCompat';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { createPath, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Subject } from 'rxjs';
import { setToasterService, ToasterService as ToolkitToasterService } from '@hevoio/hevo-ui-toolkit';
import { CompatRouter } from '../../../routing/components/compat-router/CompatRouter';
import { GlobalBlockingLoader } from '../../../loader/components/global-blocking-loader/GlobalBlockingLoader';
import { ToasterService } from '../../../toaster/services/toaster.service';
import { ToasterContainer } from '../../../toaster/components/ToasterContainer';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import useService from '../../../injector/hooks/useService';
import { GlobalDialogProvidersWrapper } from '../global-dialog-providers-wrapper/GlobalDialogProvidersWrapper';
import { RouterServiceProvider } from '../../../injector/components/router-service-provider/RouterServiceProvider';
import ThemeWrapper from '../../../theming/components/theme-wrapper/ThemeWrapper';
import {
  OPEN_SUPPORT_ON_LOAD_QUERY_PARAM_MESSAGE,
  OPEN_SUPPORT_ON_LOAD_QUERY_PARAM_TRIGGER,
  TRACKER_EMAIL_CTA_APP_LOADED,
  UTM_CONTENT,
  UTM_MEDIUM
} from '../../../core/constants/constants';
import { CustomBrowserHistory } from '../../../core/interfaces/routing';
import { SupportService } from '../../../support/interfaces/support';
import { AlgoliaService } from '../../../core/services/algolia.service';
import { BodyInteractionService } from '../../../core/services/body-interaction.service';
import { ClearCookieService } from '../../../core/services/clear-cookie.service';
import { CourierService } from '../../../core/services/courier.service';
import { DocLinkHandlerService } from '../../../docs/services/doc-link-handler.service';
import { AllowedActionsService } from '../../../core/services/entity-actions.service';
import { UnauthorisedEventService } from '../../../core/services/unauthorised-event.service';
import { VWOService } from '../../../core/services/vwo.service';
import type { CoralogixBrowserSdkConfig } from '@coralogix/browser';
import { _userServiceInstance } from '../../../core/services/user.service';
import { DashboardErrorBoundary } from '../../../edge/pipelines/pipeline-dashboard/components/dashboard-error-boundary/DashboardErrorBoundary';
import styles from './App.module.scss';
import { NavigationHistoryProvider } from '../../../routing/contexts/NavigationHistoryContext';
import { Dashboard } from '../../../dashboard/components/Dashboard';
import { StaleVersionDetectionModal } from '../../../modal/components/stale-version-detection-modal/StaleVersionDetectionModal';

declare var DASHBOARD_VERSION: string;
declare var __CORALOGIX_RUM_CONFIG__: CoralogixBrowserSdkConfig;
declare var __DEPLOYMENT_ENV__: string;

const AcceptInvite = lazy(() =>
  import('../../../auth/components/accept-invite/AcceptInvite').then(m => ({ default: m.AcceptInvite }))
);
const AccountSetup = lazy(() =>
  import('../../../auth/components/account-setup/AccountSetup').then(m => ({ default: m.AccountSetup }))
);
const EmpLoginAccountSelect = lazy(() =>
  import('../../../auth/components/emp-login-account-select/EmpLoginAccountSelect').then(m => ({
    default: m.EmpLoginAccountSelect
  }))
);
const ForgotPassword = lazy(() =>
  import('../../../auth/components/forgot-password/ForgotPassword').then(m => ({ default: m.ForgotPassword }))
);
const GoogleLogin = lazy(() =>
  import('../../../auth/components/google-login/GoogleLogin').then(m => ({ default: m.GoogleLogin }))
);
const Login = lazy(() =>
  import('../../../auth/components/login/Login').then(m => ({ default: m.Login }))
);
const LoginWithCode = lazy(() =>
  import('../../../auth/components/login-with-code/LoginWithCode').then(m => ({
    default: m.LoginWithCode
  }))
);
const RecoveryCodesPage = lazy(() =>
  import('../../../auth/components/recovery-codes-page/RecoveryCodesPage').then(m => ({
    default: m.RecoveryCodesPage
  }))
);
const ResetPassword = lazy(() =>
  import('../../../auth/components/reset-password/ResetPassword').then(m => ({
    default: m.ResetPassword
  }))
);
const RestAPIOAuth = lazy(() =>
  import('../../../auth/components/rest-api-oauth/RestAPIOAuth').then(m => ({
    default: m.RestAPIOAuth
  }))
);
const SamlLogin = lazy(() =>
  import('../../../auth/components/saml-login/SamlLogin').then(m => ({ default: m.SamlLogin }))
);
const SetupPersona = lazy(() =>
  import('../../../auth/components/setup-persona/SetupPersona').then(m => ({
    default: m.SetupPersona
  }))
);
const SubscribeMarketplace = lazy(() =>
  import('../../../auth/components/subscribe-marketplace/SubscribeMarketplace').then(m => ({
    default: m.SubscribeMarketplace
  }))
);
const TfaOtpPage = lazy(() =>
  import('../../../auth/components/tfa-otp-page/TfaOtpPage').then(m => ({
    default: m.TfaOtpPage
  }))
);
const TfaSetupPage = lazy(() =>
  import('../../../auth/components/tfa-setup-page/TfaSetupPage').then(m => ({
    default: m.TfaSetupPage
  }))
);
const VerifyEmail = lazy(() =>
  import('../../../auth/components/verify-email/VerifyEmail').then(m => ({
    default: m.VerifyEmail
  }))
);

const VersionPage = lazy(() =>
  import('../../../about/components/version-page/VersionPage').then(m => ({ default: m.VersionPage }))
);
const UnsubscribeNotificationsPage = lazy(() =>
  import('../../../containers/notification-subscription/components/unsubscribe-notifications-page/UnsubscribeNotificationsPage').then(
    m => ({ default: m.UnsubscribeNotificationsPage })
  )
);

const RouteFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }} />
);

const history = createBrowserHistory() as CustomBrowserHistory;
history['beforeListenersFired$']  = new Subject();

export function App() {
  useEffect(() => {
    const isLocalDev = typeof __DEPLOYMENT_ENV__ !== 'undefined' && __DEPLOYMENT_ENV__ === 'local';
    const hasConfig = typeof __CORALOGIX_RUM_CONFIG__ !== 'undefined';

    if (isLocalDev || !hasConfig) return;

    // Loaded on demand so the RUM SDK (and rrweb/OpenTelemetry under it) stays off the first-load bundle
    import('@coralogix/browser').then(({ CoralogixRum }) => {
      const notInited = !CoralogixRum.isInited;

      if (!notInited) return;

      CoralogixRum.init({
        ...__CORALOGIX_RUM_CONFIG__,
        version: DASHBOARD_VERSION.replace(/ /g, '_'),
        ignoreErrors: [/Promise Cancelled/],
        sessionRecordingConfig: {
          enable: true,
          autoStartSessionRecording: true,
          recordConsoleEvents: true,
          sessionRecordingSampleRate: 100,
          maskInputOptions: { password: true }
        }
      });

      // users/me can land before this on-demand SDK does, and the context update it triggers is
      // one-shot, so attach an already signed-in user here too
      const user = _userServiceInstance?.getUser();

      if (user) {
        _userServiceInstance.updateCoralogixRumContext(user);
      }
    }).catch(() => {
      // RUM is optional: a failed SDK download must not surface as an unhandled rejection
    });
  }, []); // Run once on mount
  
  return (
    <CompatRouter history={history}>
      <RouterServiceProvider>
        <NavigationHistoryProvider>
          <AppInner />
        </NavigationHistoryProvider>
      </RouterServiceProvider>
    </CompatRouter>
  );
}

export function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventTrack } = useAnalyticsTracker();

  const supportService = useService(SupportService);
  const toasterService = useService(ToasterService);
  const vwoService = useService(VWOService);
  const angularticsHubspotService = useService(Angulartics2Hubspot);
  const angularticsSegmentService = useService(Angulartics2Segment);
  const angularticsMixpanelService = useService(Angulartics2Mixpanel);
  useService(AllowedActionsService);
  useService(UnauthorisedEventService);

  /**
   * To initialise global reference of these services;
   */
  useService(CourierService);
  useService(DocLinkHandlerService);
  useService(AlgoliaService);

  /**
   * Initialise logic in constructor
   */
  useService(ClearCookieService);
  useService(BodyInteractionService);
  useMemo(() => {
    angularticsHubspotService.startTracking();
    angularticsSegmentService.startTracking();
    angularticsMixpanelService.startTracking();

    vwoService.triggerWebsiteSignupConversion();

    setToasterService(toasterService as unknown as ToolkitToasterService);
   
  }, []);


  useEffect(() => {
    document.dispatchEvent(new Event('initialLoad'));

    const searchParams = new URLSearchParams(location.search);

    const openSupportOnLoad = !!searchParams.get(OPEN_SUPPORT_ON_LOAD_QUERY_PARAM_TRIGGER);
    const openSupportOnLoadMessage = searchParams.get(OPEN_SUPPORT_ON_LOAD_QUERY_PARAM_MESSAGE);
    const utmMedium = searchParams.get(UTM_MEDIUM);
    const utmContent = searchParams.get(UTM_CONTENT);

    if (utmMedium === 'EMAIL' && utmContent) {
      const properties = JSON.parse(utmContent);

      eventTrack({
        action: TRACKER_EMAIL_CTA_APP_LOADED,
        properties: properties
      });
    }

    let replaceHistory = false;

    if (openSupportOnLoad) {
      replaceHistory = true;

      searchParams.delete(OPEN_SUPPORT_ON_LOAD_QUERY_PARAM_TRIGGER);
      searchParams.delete(OPEN_SUPPORT_ON_LOAD_QUERY_PARAM_MESSAGE);
    }

    if (utmMedium || utmContent) {
      replaceHistory = true;

      searchParams.delete(UTM_MEDIUM);
      searchParams.delete(UTM_CONTENT);
    }

    if (replaceHistory) {
      /**
       * Explicitly replacing location history entry to eagerly change the browser url.
       * This is done to avoid auth guard from using the initial navigation url for setting up
       * the next query param.
       */
      const url = createPath({
        pathname: location.pathname,
        hash: location.hash,
        search: searchParams.toString()
      });

      window.history.replaceState({}, '', url);
      navigate(url);
    }

    if (openSupportOnLoad) {
      supportService.show(openSupportOnLoadMessage);
    }
  }, []);



  return (
      <ThemeWrapper>
        <GlobalDialogProvidersWrapper>
          <GlobalBlockingLoader />

          <ToasterContainer />

          {/*
            Last line of defence for a route or lazy dialog that fails to load or render: without it
            the throw unmounts the whole tree and leaves a blank page. Reloading fetches the current
            build, which is what recovers a stale tab after a deploy.
          */}
          <DashboardErrorBoundary
            surface='app'
            title='Something went wrong loading this page'
            subTitle='Reload to get the latest version of Hevo.'
            retryLabel='Reload'
            dataId='app-error'
            reportKey='app.render-error'
            containerClassName={styles.errorFallback}
            onReset={() => window.location.reload()}
          >
          <Suspense fallback={<RouteFallback />}>
          <StaleVersionDetectionModal />
          <Routes>
            <Route
              key='unsubscribe-notification'
              path='/notification-preference/unsubscribe/:unsubscriptionType/:identifier'
              element={<UnsubscribeNotificationsPage />}
            />
            <Route key='about-version' path='/about/version' element={<VersionPage />} />
            <Route key='login-with-code' path='/login/code/:code' element={<LoginWithCode />} />

            <Route key='login' path='/login' element={<Login />} />

            <Route key='forgot-password' path='/forgot-password' element={<ForgotPassword />} />

            <Route
              key='emp-login-ac-select'
              path='/emp-login-ac-select'
              element={<EmpLoginAccountSelect />}
            />

            <Route key='reset' path='/reset' element={<ResetPassword />} />

            <Route key='accept-invite' path='/accept-invite' element={<AcceptInvite />} />

            <Route key='setup-account' path='/setup-account' element={<AccountSetup />} />

            <Route key='verify-email' path='/verify-email' element={<VerifyEmail />} />

            <Route key='saml-login' path='/saml-login' element={<SamlLogin />} />

            <Route key='google-login' path='/google-login' element={<GoogleLogin />} />

            <Route key='tfa-otp' path='/tfa/otp' element={<TfaOtpPage />} />

            <Route key='tfa-qr' path='/tfa/qr' element={<TfaSetupPage />} />

            <Route
              key='tfa-recovery-codes'
              path='/tfa/recovery-codes'
              element={<RecoveryCodesPage />}
            />

            <Route key='rest-api-oauth' path='/rest-api/oauth' element={<RestAPIOAuth />} />

            <Route key='setup-persona' path='/setup-persona' element={<SetupPersona />} />

            <Route
              key='subscribe-marketplace'
              path='/subscribe-marketplace'
              element={<SubscribeMarketplace />}
            />
            <Route key='dashboard' path='*' element={<Dashboard />} />
          </Routes>
          </Suspense>
          </DashboardErrorBoundary>

        
      </GlobalDialogProvidersWrapper>
      </ThemeWrapper>
  );
}
